import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BookOpen,
  Heart,
  DollarSign,
  Trophy,
  Clock,
  Bell,
  TrendingUp,
  Play,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const ReaderDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [library, setLibrary] = useState([]);
  const [recentUpdates, setRecentUpdates] = useState([]);
  const [continueReading, setContinueReading] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [librarySort, setLibrarySort] = useState('recent');
  
  // Ref to prevent duplicate fetches
  const hasFetched = useRef(false);

  useEffect(() => {
    // Use user?.id to prevent re-fetch on object reference change
    if (user?.id && !hasFetched.current) {
      hasFetched.current = true;
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchLibrary(librarySort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [librarySort]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    
    try {
      // Fail-soft: each request wrapped individually
      const [dashboardRes, libraryRes, updatesRes, continueRes, recommendationsRes] = await Promise.all([
        axios.get(`${API}/user/dashboard`, { headers, timeout: 10000 }).catch(() => ({ data: null })),
        axios.get(`${API}/user/library?sort=recent`, { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/recent-updates?limit=12`, { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/user/continue-reading?limit=8`, { headers, timeout: 10000 }).catch(() => ({ data: [] })),
        axios.get(`${API}/homepage/trending?limit=6`, { timeout: 10000 }).catch(() => ({ data: [] })),
      ]);

      setDashboardStats(dashboardRes.data);
      setLibrary(libraryRes.data || []);
      setRecentUpdates(updatesRes.data || []);
      setContinueReading(continueRes.data || []);
      setRecommendations(recommendationsRes.data || []);
      setLoading(false);
    } catch (error) {
      console.warn('Error fetching dashboard:', error);
      // Fail soft - don't show error toast, just set empty state
      setLoading(false);
      hasFetched.current = false; // Allow retry on refresh
    }
  };

  const fetchLibrary = async (sort) => {
    try {
      const response = await axios.get(`${API}/user/library?sort=${sort}`);
      setLibrary(response.data);
    } catch (error) {
      console.error('Error fetching library:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading your dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-b border-orange-500/30">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-3xl font-black">
                {user.email?.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h1 className="text-3xl font-black">Welcome back!</h1>
                <p className="text-gray-400">{user.email}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="hidden md:grid grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">{dashboardStats?.subscriptions_count || 0}</div>
                <div className="text-gray-400 text-sm">Subscribed Series</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">{dashboardStats?.badges_earned || 0}</div>
                <div className="text-gray-400 text-sm">Badges Earned</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400">{dashboardStats?.episodes_read || 0}</div>
                <div className="text-gray-400 text-sm">Episodes Read</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <Tabs defaultValue="library" className="space-y-6">
          <TabsList className="bg-gray-900 border border-gray-800">
            <TabsTrigger value="library" className="data-[state=active]:bg-orange-500">
              <BookOpen className="w-4 h-4 mr-2" />
              My Library
            </TabsTrigger>
            <TabsTrigger value="updates" className="data-[state=active]:bg-orange-500">
              <Bell className="w-4 h-4 mr-2" />
              Recent Updates
            </TabsTrigger>
            <TabsTrigger value="continue" className="data-[state=active]:bg-orange-500">
              <Play className="w-4 h-4 mr-2" />
              Continue Reading
            </TabsTrigger>
            <TabsTrigger value="discover" className="data-[state=active]:bg-orange-500">
              <TrendingUp className="w-4 h-4 mr-2" />
              Discover
            </TabsTrigger>
          </TabsList>

          {/* My Library Tab */}
          <TabsContent value="library">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">My Library ({library.length})</h2>
              <Select value={librarySort} onValueChange={setLibrarySort}>
                <SelectTrigger className="w-48 bg-gray-900 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-white">
                  <SelectItem value="recent">Recently Updated</SelectItem>
                  <SelectItem value="favorites">Favorites</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {library.map((series) => (
                <Link key={series.id} to={`/series/${series.id}`} className="group">
                  <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all">
                    <CardContent className="p-0">
                      <div className="relative aspect-[2/3] bg-gray-800 rounded-t-lg overflow-hidden">
                        <img
                          src={series.cover_url}
                          alt={series.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {/* Progress Bar */}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-2">
                          <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                            <div
                              className="bg-gradient-to-r from-orange-500 to-red-500 h-2 rounded-full"
                              style={{ width: `${series.progress?.percentage || 0}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-300">
                            {series.progress?.completed || 0}/{series.progress?.total || 0} episodes
                          </p>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="text-white font-medium text-sm line-clamp-2 mb-1">
                          {series.title}
                        </h3>
                        <p className="text-gray-400 text-xs">{series.genres?.slice(0, 2).join(', ')}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {library.length === 0 && (
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-12 text-center">
                  <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Your library is empty</h3>
                  <p className="text-gray-400 mb-6">Start following your favorite series!</p>
                  <Link to="/browse">
                    <Button className="bg-orange-500 hover:bg-orange-600">Browse Comics</Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Recent Updates Tab */}
          <TabsContent value="updates">
            <h2 className="text-2xl font-bold mb-6">Recent Updates</h2>
            <div className="space-y-4">
              {recentUpdates.map((chapter) => (
                <Link key={chapter.id} to={`/series/${chapter.series_id}/read/${chapter.id}`}>
                  <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-4">
                        <img
                          src={chapter.series_cover}
                          alt={chapter.series_title}
                          className="w-16 h-24 object-cover rounded"
                        />
                        <div className="flex-1">
                          <Badge className="bg-green-500 text-white mb-2">NEW</Badge>
                          <h3 className="text-white font-bold mb-1">{chapter.series_title}</h3>
                          <p className="text-gray-300 text-sm mb-2">
                            Episode {chapter.chapter_number}: {chapter.title}
                          </p>
                          <p className="text-gray-400 text-xs">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(chapter.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                          Read Now
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>

          {/* Continue Reading Tab */}
          <TabsContent value="continue">
            <h2 className="text-2xl font-bold mb-6">Continue Reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {continueReading.map((item) => (
                <Link key={item.chapter.id} to={`/series/${item.series.id}/read/${item.chapter.id}`}>
                  <Card className="bg-gray-900 border-gray-800 hover:border-orange-500 transition-all group">
                    <CardContent className="p-0">
                      <div className="flex">
                        <img
                          src={item.series.cover_url}
                          alt={item.series.title}
                          className="w-32 h-48 object-cover"
                        />
                        <div className="flex-1 p-4">
                          <h3 className="text-white font-bold text-lg mb-2 group-hover:text-orange-400">
                            {item.series.title}
                          </h3>
                          <p className="text-gray-300 text-sm mb-3">
                            Episode {item.chapter.chapter_number}: {item.chapter.title}
                          </p>
                          {!item.progress.completed && (
                            <Badge className="bg-orange-500 text-white mb-3">In Progress</Badge>
                          )}
                          <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                            <Play className="w-4 h-4 mr-2" />
                            {item.progress.completed ? 'Read Again' : 'Resume Reading'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </TabsContent>

          {/* Discover Tab */}
          <TabsContent value="discover">
            <h2 className="text-2xl font-bold mb-6">Recommended for You</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {recommendations.map((series) => (
                <Link key={series.id} to={`/series/${series.id}`} className="group">
                  <div className="relative aspect-[2/3] bg-gray-800 rounded-lg overflow-hidden mb-2">
                    <img
                      src={series.cover_url}
                      alt={series.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="text-white font-medium text-sm line-clamp-2 mb-1 group-hover:text-orange-400">
                    {series.title}
                  </h3>
                  <p className="text-gray-400 text-xs">{series.genres?.slice(0, 2).join(', ')}</p>
                </Link>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ReaderDashboard;
